/**
 * Theme Switcher Service
 *
 * Pattern adapted from Eaze Customs:
 * 1. Sets [data-theme="<name>"] on <html> to activate SCSS CSS variables
 * 2. Persists choice in localStorage
 */
import { Injectable, signal } from '@angular/core';

/** Available theme identifiers applied via `data-theme` on `<html>`. */
export const THEMES = ['primary', 'secondary'] as const;

/** Union of supported theme names. */
export type ThemeName = (typeof THEMES)[number];

const DEFAULT_THEME: ThemeName = 'primary';
const STORAGE_KEY = 'ds-theme';

/** Display metadata and swatch colors for each theme option. */
export const THEME_META: Record<
  ThemeName,
  { label: string; description: string; swatch: string; swatchAlt: string }
> = {
  primary: {
    label: 'Primary',
    description: 'Soft lavender with vibrant violet',
    swatch: '#7c5cff',
    swatchAlt: '#f3f0ff',
  },
  secondary: {
    label: 'Secondary',
    description: 'Teal medical accent',
    swatch: '#0d9488',
    swatchAlt: '#eaeef4',
  },
};

/** Manages active UI theme and persists the choice in localStorage. */
@Injectable({ providedIn: 'root' })
export class ThemeSwitcherService {
  /** Currently active theme name. */
  readonly theme = signal<ThemeName>(DEFAULT_THEME);

  /** All selectable theme names. */
  readonly themes: readonly ThemeName[] = THEMES;

  /** Label, description, and swatch colors keyed by theme name. */
  readonly themeMeta = THEME_META;

  constructor() {
    this.init();
  }

  /**
   * Restores the saved theme from localStorage or applies the default.
   * @returns void
   */
  init(): void {
    const saved = localStorage.getItem(STORAGE_KEY) as ThemeName | null;
    const name: ThemeName =
      saved && (THEMES as readonly string[]).includes(saved)
        ? (saved as ThemeName)
        : DEFAULT_THEME;
    this.applyTheme(name);
  }

  /**
   * Persists and applies the given theme.
   * @param name - Theme identifier to activate
   * @returns void
   */
  setTheme(name: ThemeName): void {
    localStorage.setItem(STORAGE_KEY, name);
    this.applyTheme(name);
  }

  /** @returns The currently active theme name. */
  getTheme(): ThemeName {
    return this.theme();
  }

  /**
   * Updates the signal and sets `data-theme` on the document root.
   * @param name - Theme identifier to apply
   * @returns void
   */
  private applyTheme(name: ThemeName): void {
    this.theme.set(name);
    document.documentElement.setAttribute('data-theme', name);
  }
}
